<template>
  <ContainerBasic container-type="FULL_WIDTH">
    <ContainerBasic container-type="PAGE">
      <Headline
        :headline="blockData.headline"
        :level="'h2'"
        :has-decoration="true"
      />

      <div
        class="wrapper px-md desktop:md:px-xl touch:md:px-md desktop:lg:px-md"
      >
        <div class="items mt-lg">
          <div
            v-for="(item, index) of blockData.items"
            :key="index"
            class="divide-y-2 item divide-vdv-border-light"
          >
            <div class="h-[84px]">
              <div
                class="item-dots rounded-[16px] border-2 border-dotted w-[32px] h-[32px] border-black"
              >
                <div
                  class="bg-black w-[24px] h-[24px] rounded-xl m-[2px]"
                ></div>
              </div>
            </div>

            <div class="pb-0 touch:md:pb-md lg:pb-md">
              <div
                class="item-header flex items-center -translate-y-1/2 w-[calc(100%-32px)] touch:md:w-full lg:w-full"
              >
                <div
                  class="absolute z-10 text-4xl font-bold bg-white item-header-index md:text-6xl text-vdv-border-light -right-8 touch:md:right-auto lg:right-auto"
                >
                  <span>{{ index + 1 }}</span>
                </div>
                <atom-circle-icon
                  :icon="item.icon"
                  class=""
                  background-color="#346E73"
                ></atom-circle-icon>
              </div>

              <div class="mt-[-40px] touch:md:min-h-[180px] lg:min-h-[180px]">
                <Headline
                  class="mb-sm"
                  :headline="item.headline"
                  :level="'h4'"
                  :has-decoration="true"
                />

                <WYSIWYGContentRaw
                  :content="item.text"
                  :no-container-padding="true"
                ></WYSIWYGContentRaw>

                <div v-if="item.cta" class="mb-0 mt-md">
                  <CallToAction :block-data="item.cta"></CallToAction>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContainerBasic>

    <ContainerBasic
      v-if="blockData.banner"
      class="mt-3xl"
      container-type="FULL_WIDTH"
    >
      <div ref="bannerNode">
        <div class="h-[30px]" style="background-color: #628e92" />
        <div
          class="min-h-[329px] bg-left-bottom md:bg-bottom md:bg-contain relative text-white text-center bg-no-repeat bg-activesection-base"
          :style="`background-image: url(${imageUrl})`"
        >
          <div
            class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[52.95%] text-[#b8cbce] w-[260.5px]"
          >
            <div class="relative flex items-center justify-center">
              <img
                src="@/assets/images/icon_badge.svg"
                class="mx-auto"
                loading="lazy"
              />
              <div class="absolute">
                <Icon :icon="blockData.banner.icon" classes="text-7xl" />
              </div>
            </div>
          </div>
          <div class="pt-[100px]">
            <Headline
              classes="font-josefin text-[24px] touch:md:text-[40px] lg:text-[40px]"
              text-color="white"
              :headline="blockData.banner.headline"
              level="h2"
            />

            <WYSIWYGContentRaw
              class="mt-md"
              :content="blockData.banner.text"
            ></WYSIWYGContentRaw>

            <div v-if="blockData.banner.cta" class="mt-md">
              <CallToAction :block-data="blockData.banner.cta"></CallToAction>
            </div>

            <img
              src="@/assets/images/vdv_menschzumensch_claim_white.svg"
              class="mt-md pb-[170px] touch:md:pb-md lg:pb-md w-[210px] mx-auto"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </ContainerBasic>
  </ContainerBasic>
</template>
<script lang="ts" setup>
import { ref } from 'vue';
import ContainerBasic from '@/components/components/container-basic.vue';
import AtomCircleIcon from '@/components/atom/circle-icon.vue';
import CallToAction from '~~/src/components/block/cta/vdv/CallToAction.vue';
import { Headline, WYSIWYGContentRaw, Icon } from '~/complib';
import { mq_breakpointIsMobile } from '@/injectionSymbols';
import type { FlowchartData } from '../blockType';

const isMobile = inject(mq_breakpointIsMobile);

const props = defineProps({
  blockData: {
    type: Object as PropType<FlowchartData>,
    required: true,
    default: {} as FlowchartData,
  },
});

const bannerNode = ref(null);
const imageUrl = computed(() =>
  props.blockData.image
    ? props.blockData.image[isMobile.value ? 'mobile' : 'desktop'].url
    : '',
);
</script>

<style scoped lang="postcss">
.item {
  @apply relative ml-md touch:md:ml-0 touch:md:w-1/2 lg:ml-0 lg:w-1/2;

  :deep(h4) {
    @apply pr-8;
  }

  @screen lg {
    :deep(h4) {
      @apply pr-0;
    }
  }
}

.item:not(:first-child) {
  @apply mt-md touch:md:mt-[60px] lg:mt-[60px];
}

.item:nth-child(2) {
  @apply touch:md:mt-[200px] lg:mt-[200px];
}

.item:nth-child(even) {
  @apply touch:md:pl-xl lg:pl-xl xl:pl-3xl touch:md:float-right lg:float-right;
  .item-dots {
    @apply absolute z-10 top-[84px] left-[-30px] touch:md:left-0 lg:left-0 -translate-x-1/2 -translate-y-1/2;
  }

  .item-header {
    @apply flex-row-reverse touch:md:flex-row lg:flex-row;

    .item-header-index {
      @apply pr-0 pl-sm touch:md:pl-0 touch:md:pr-md lg:pl-0 lg:pr-md;
    }
  }
}

.item:nth-child(odd) {
  @apply touch:md:pr-xl lg:pr-xl xl:pr-3xl touch:md:float-left lg:float-left;
  .item-dots {
    @apply absolute z-10 top-[84px] left-[-30px] touch:md:left-auto touch:md:right-0 lg:left-auto lg:right-0 -translate-x-1/2 touch:md:translate-x-1/2 lg:translate-x-1/2 -translate-y-1/2;
  }

  .item-header {
    @apply flex-row-reverse;

    .item-header-index {
      @apply pr-0 pl-sm;
    }
  }
}

.items {
  &:before {
    content: '';
    @apply absolute w-[4px] bg-vdv-border-light top-[84px] bottom-0 touch:md:left-1/2 lg:left-1/2 translate-x-[-50%];
  }

  &:after {
    content: '';
    @apply block clear-both;
  }
}

.wrapper {
  @apply relative;
}
</style>
